import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Autocomplete,
    Alert,
    FormControlLabel,
    Paper,
    RadioGroup,
    Radio,
    CircularProgress
} from "@mui/material";
import {
    Close as CloseIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    AdminPanelSettings as AdminIcon,
    Public as PublicIcon,
    Lock as LockIcon,
    Security as SecurityIcon
} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {useAuth} from "../../contexts/AuthContext";
import { getCompanies } from "../../services/system/CompanyService";

const EditPermissionsDialog = ({
                                   open,
                                   onClose,
                                   onSave,
                                   template,
                               }) => {
    // Always a system resource in this dialog — a super-admin only manages system/global templates.
    const [isSystemResource] = useState(true);
    const [assignmentType, setAssignmentType] = useState('SPECIFIC');
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [newCompanyId, setNewCompanyId] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const {isSuperAdmin} = useAuth();

    // Load companies when dialog opens
    useEffect(() => {
        if (open) {
            loadCompanies();
        }
    }, [open]);

    // Load companies from API
    const loadCompanies = async () => {
        setCompaniesLoading(true);
        try {
            const response = await getCompanies();
            if (response && response.data) {
                // Transform to match expected structure: {id, name}
                const transformedCompanies = response.data.map(company => ({
                    id: company.id,
                    name: company.companyName || company.name
                }));
                setCompanies(transformedCompanies);
            }
        } catch (error) {
            console.error('Error loading companies:', error);
            setErrors({ ...errors, load: 'שגיאה בטעינת החברות' });
        } finally {
            setCompaniesLoading(false);
        }
    };

    useEffect(() => {
        if (template) {
            // Map companyPermissions to selectedCompanies (extract IDs)
            const companyIds = template.companyPermissions ?
                template.companyPermissions.map(cp => cp.companyId) : [];

            // Determine assignment type based on companyPermissions
            if (template.companyPermissions === null) {
                setAssignmentType('ALL');
                setSelectedCompanies([]);
            } else if (template.companyPermissions && template.companyPermissions.length === 0) {
                setAssignmentType('NONE');
                setSelectedCompanies([]);
            } else {
                setAssignmentType('SPECIFIC');
                setSelectedCompanies(companyIds);
            }
        }
    }, [template]);

    const handleAssignmentTypeChange = (event) => {
        const type = event.target.value;

        setAssignmentType(type);
        if (type === 'ALL' || type === 'NONE') {
            setSelectedCompanies([]);
        }
        setErrors({ ...errors, assignment: null });
    };

    const handleAddCompany = () => {
        if (!newCompanyId || selectedCompanies.includes(newCompanyId)) {
            setErrors({ ...errors, company: 'יש לבחור חברה שלא נבחרה כבר' });
            return;
        }

        // For non-system resources (regular templates), allow only one company
        if (!isSystemResource) {
            setSelectedCompanies([newCompanyId]);
        } else {
            setSelectedCompanies([...selectedCompanies, newCompanyId]);
        }

        setNewCompanyId(null);
        setErrors({ ...errors, company: null });
    };

    const handleRemoveCompany = (companyId) => {
        setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
    };

    const handleSave = async () => {
        // Clear previous errors
        setErrors({});

        // Validation
        if (assignmentType === 'SPECIFIC' && selectedCompanies.length === 0) {
            setErrors({ assignment: 'יש לבחור לפחות חברה אחת' });
            return;
        }

        // Additional validation for non-system resources (regular templates)
        if (!isSystemResource) {
            if (assignmentType !== 'SPECIFIC') {
                setErrors({ assignment: 'תבנית רגילה חייבת להיות משויכת לחברה ספציפית' });
                return;
            }
            if (selectedCompanies.length !== 1) {
                setErrors({ assignment: 'תבנית רגילה חייבת להיות משויכת לחברה אחת בלבד' });
                return;
            }
        }

        setLoading(true);
        try {
            let companyPermissions;

            switch (assignmentType) {
                case 'ALL':
                    companyPermissions = null; // null = ALL companies (matches Java model)
                    break;
                case 'NONE':
                    companyPermissions = []; // empty array = NONE (system admin only)
                    break;
                case 'SPECIFIC':
                    // Create CompanyPermissions objects matching your Java model
                    companyPermissions = selectedCompanies.map(companyId => {
                        const company = companies.find(c => c.id === companyId);
                        return {
                            companyId: companyId,
                            companyName: company?.name || company?.companyName || `Company ${companyId}`
                        };
                    });
                    break;
                default:
                    companyPermissions = [];
            }

            // Create the updated template object
            const updatedTemplate = {
                ...template,
                systemResource: isSystemResource,
                companyPermissions: companyPermissions
            };

            // Call the onSave callback with the updated template
            await onSave(updatedTemplate);

            // Success - dialog will be closed by parent component

        } catch (error) {
            console.error('Error saving permissions:', error);
            setErrors({ save: error.message || 'שגיאה בשמירת ההרשאות' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setErrors({});
        setCompanies([]); // Clear companies when closing
        onClose();
    };

    const getSelectedCompanyNames = () => {
        return selectedCompanies.map(id => {
            const company = companies.find(c => c.id === id);
            return company?.name || `Company ${id}`;
        });
    };

    const getAvailableCompanies = () => {
        return companies.filter(company => !selectedCompanies.includes(company.id));
    };

    const getAssignmentSummary = () => {
        switch (assignmentType) {
            case 'ALL':
                return 'תבנית זמינה לכל החברות במערכת';
            case 'NONE':
                return 'תבנית זמינה רק למנהל המערכת';
            case 'SPECIFIC':
                if (!isSystemResource) {
                    return `תבנית רגילה משויכת לחברה: ${selectedCompanies.length === 1 ? getSelectedCompanyNames()[0] : 'לא נבחרה חברה'}`;
                }
                return `תבנית זמינה ל-${selectedCompanies.length} חברות`;
            default:
                return '';
        }
    };

    if (!isSuperAdmin) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ direction: 'rtl' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon sx={{ color: '#64748B' }} />
                        <Typography>ניהול הרשאות</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info">
                        רק מנהל מערכת יכול לערוך הרשאות תבניות
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ direction: 'rtl' }}>
                    <Button onClick={handleClose}>סגור</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '12px',
                    direction: 'rtl',
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#FCFCFD',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon sx={{ color: '#64748B' }} />
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '18px',
                            color: '#1E293B',
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        ניהול הרשאות - {template?.nameHeb}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} sx={{ color: '#64748B' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ backgroundColor: '#FFFFFF', padding: 0 }}>
                {errors.save && (
                    <Alert severity="error" sx={{ m: 2, mb: 0 }}>
                        {errors.save}
                    </Alert>
                )}

                {errors.load && (
                    <Alert severity="error" sx={{ m: 2, mb: 0 }}>
                        {errors.load}
                        <Button
                            size="small"
                            onClick={loadCompanies}
                            sx={{ ml: 1 }}
                            disabled={companiesLoading}
                        >
                            נסה שוב
                        </Button>
                    </Alert>
                )}

                {companiesLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 2 }}>טוען חברות...</Typography>
                    </Box>
                )}

                {/* Role indicator */}
                <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                    <Chip
                        icon={<AdminIcon />}
                        label="מנהל מערכת"
                        size="small"
                        sx={{
                            backgroundColor: '#DBEAFE',
                            color: '#1E40AF'
                        }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, color: '#64748B' }}>
                        ניתן לנהל הרשאות גישה לתבניות
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    {/* Templates managed here are always SYSTEM resources (a super-admin only manages
                        global/system templates). The old "regular template / specific company" mode
                        was removed — a super-admin has no company context to assign a company-owned
                        template to. `isSystemResource` is therefore always true below. */}

                    {/* Assignment Type Selection */}
                    <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                        זמינות התבנית
                    </Typography>

                    <FormControl component="fieldset" sx={{ mb: 3 }}>
                        <RadioGroup
                            value={assignmentType}
                            onChange={handleAssignmentTypeChange}
                        >
                            <FormControlLabel
                                value="ALL"
                                control={<Radio />}
                                disabled={!isSystemResource}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PublicIcon sx={{ fontSize: 16 }} />
                                        <Typography sx={{ color: !isSystemResource ? '#CBD5E1' : 'inherit' }}>
                                            כל החברות
                                        </Typography>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="SPECIFIC"
                                control={<Radio />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BusinessIcon sx={{ fontSize: 16 }} />
                                        <Typography>
                                            {!isSystemResource ? 'חברה ספציפית (נדרש)' : 'חברות ספציפיות'}
                                        </Typography>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="NONE"
                                control={<Radio />}
                                disabled={!isSystemResource}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LockIcon sx={{ fontSize: 16 }} />
                                        <Typography sx={{ color: !isSystemResource ? '#CBD5E1' : 'inherit' }}>
                                            מנהל מערכת בלבד
                                        </Typography>
                                    </Box>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    <Alert severity={isSystemResource ? "warning" : "info"} sx={{ mb: 3 }}>
                        {getAssignmentSummary()}
                    </Alert>

                    {errors.assignment && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.assignment}
                        </Alert>
                    )}

                    {/* Company Selection for SPECIFIC type */}
                    {assignmentType === 'SPECIFIC' && !companiesLoading && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                {isSystemResource ? `בחירת חברות (${selectedCompanies.length})` : 'בחירת חברה'}
                            </Typography>

                            {!isSystemResource && selectedCompanies.length === 1 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    תבניות רגילות יכולות להיות משויכות רק לחברה אחת. כדי לשנות חברה, יש להסיר את החברה הנוכחית ולבחור חברה חדשה.
                                </Alert>
                            )}

                            {/* Add Company */}
                            {(!isSystemResource ? selectedCompanies.length === 0 : true) && (
                                <Box sx={{ mb: 3, p: 2, backgroundColor: '#F8FAFC', borderRadius: 2 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        {isSystemResource ? 'הוספת חברה' : 'בחירת חברה'}
                                    </Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={9}>
                                            <RTL>
                                                <Autocomplete
                                                    options={getAvailableCompanies()}
                                                    getOptionLabel={(option) => option.name}
                                                    value={companies.find(c => c.id === newCompanyId) || null}
                                                    onChange={(event, newValue) => {
                                                        setNewCompanyId(newValue?.id || null);
                                                        if (errors.company) {
                                                            setErrors({ ...errors, company: null });
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={isSystemResource ? "בחר חברה להוספה" : "בחר חברה"}
                                                            error={!!errors.company}
                                                            helperText={errors.company}
                                                            disabled={companies.length === 0}
                                                        />
                                                    )}
                                                    noOptionsText="אין חברות זמינות"
                                                />
                                            </RTL>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Button
                                                onClick={handleAddCompany}
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                fullWidth
                                                sx={{ fontFamily: '"Heebo", "Roboto", sans-serif' }}
                                                disabled={!newCompanyId || companies.length === 0}
                                            >
                                                {!isSystemResource ? 'בחר' : 'הוסף'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Selected Companies List */}
                            {selectedCompanies.length === 0 ? (
                                <Typography sx={{ color: '#64748B', textAlign: 'center', py: 3 }}>
                                    {!isSystemResource ? 'יש לבחור חברה' : 'לא נבחרו חברות'}
                                </Typography>
                            ) : (
                                <List>
                                    {selectedCompanies.map((companyId) => {
                                        const company = companies.find(c => c.id === companyId);
                                        return (
                                            <ListItem
                                                key={companyId}
                                                sx={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: 2,
                                                    mb: 1,
                                                    backgroundColor: '#FFFFFF'
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <BusinessIcon sx={{ fontSize: 16, color: '#64748B' }} />
                                                            <Typography sx={{ fontWeight: 500 }}>
                                                                {company?.name || `Company ${companyId}`}
                                                            </Typography>
                                                            {isSystemResource && (
                                                                <Chip
                                                                    label="תבנית מערכת"
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: '#FEF3C7',
                                                                        color: '#92400E',
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary="גישה מלאה לקריאה"
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleRemoveCompany(companyId)}
                                                        sx={{
                                                            color: '#EF4444',
                                                            '&:hover': { backgroundColor: '#FEE2E2' }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    )}

                    {/* Summary */}
                    <Paper sx={{ p: 2, mt: 3, backgroundColor: '#F1F5F9' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            סיכום הרשאות:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                            • סוג תבנית: {isSystemResource ? 'תבנית מערכת' : 'תבנית רגילה'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                            • זמינות: {getAssignmentSummary()}
                        </Typography>
                        {assignmentType === 'SPECIFIC' && selectedCompanies.length > 0 && (
                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                • חברות נבחרות: {getSelectedCompanyNames().join(', ')}
                            </Typography>
                        )}
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    backgroundColor: '#FCFCFD',
                    borderTop: '1px solid #E2E8F0',
                    padding: '16px 24px',
                    direction: 'rtl'
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{
                        color: 'secondary',
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}
                >
                    ביטול
                </Button>
                <RTL>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading || companiesLoading}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: '#FFFFFF',
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            '&:hover': {
                                backgroundColor: '#475569'
                            }
                        }}
                    >
                        {loading ? 'שומר...' : 'שמירת הרשאות'}
                    </Button>
                </RTL>
            </DialogActions>
        </Dialog>
    );
};

export default EditPermissionsDialog;